import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

import type { R1FundBreakdownEntry } from "@/features/R1Dashboard/services/api";

const PALETTE = [
  "#0f62fe",
  "#6929c4",
  "#005d5d",
  "#9f1853",
  "#fa4d56",
  "#570408",
  "#198038",
  "#002d9c",
  "#ee538b",
  "#b28600",
  "#009d9a",
  "#012749",
  "#8a3800",
  "#a56eff",
  "#004d5b",
];

const RADIAN = Math.PI / 180;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderPieLabel(props: any) {
  const { cx, cy, midAngle, outerRadius, payload, percent } = props;
  if (!percent || percent < 0.05) return null;
  const name = String(payload?.label ?? "");
  const radius = (outerRadius ?? 100) + 22;
  const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {name.length > 18 ? name.slice(0, 16) + "…" : name} (
      {(percent * 100).toFixed(0)}%)
    </text>
  );
}

interface BreakdownPieChartProps {
  title: string;
  data: R1FundBreakdownEntry[];
  fillRate: number;
  paletteOffset?: number;
  unitPlural?: string;
  isLoading?: boolean;
  isError?: boolean;
}

export default function BreakdownPieChart({
  title,
  data,
  fillRate,
  paletteOffset = 0,
  unitPlural = "funds",
  isLoading = false,
  isError = false,
}: BreakdownPieChartProps) {
  if (isLoading || isError || data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
        <p className="text-xs text-gray-400 text-center py-8">
          {isError
            ? "Couldn't load this data — please retry."
            : isLoading
              ? "Loading…"
              : "No data"}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
      <div className="[&_svg]:outline-none [&_svg:focus]:outline-none [&_path:focus]:outline-none">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart style={{ outline: "none" }}>
            <Pie
              data={data}
              nameKey="label"
              dataKey="count"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={95}
              paddingAngle={2}
              label={renderPieLabel}
              labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
              isAnimationActive={false}
            >
              {data.map((_, i) => (
                <Cell
                  key={i}
                  fill={PALETTE[(i + paletteOffset) % PALETTE.length]}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(8px)",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 12,
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={(_value: unknown, _name: unknown, entry: any) => [
                `${entry.payload.count} ${unitPlural} (${entry.payload.percentage}%)`,
                entry.payload.label,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-center text-[11px] text-gray-400">
        Fill rate: {fillRate}%
      </p>
    </div>
  );
}
